/**
 * Core Avatar & AvatarGroup Components
 *
 * User avatar with image fallback, initials generation, status indicator,
 * size variants, and stacked group layout. Built on MUI Avatar.
 *
 * @example
 *   <Avatar name="John Doe" src="/img/john.png" size="md" status="online" />
 *   <AvatarGroup max={3}>
 *     <Avatar name="John" /><Avatar name="Jane" /><Avatar name="Bob" />
 *   </AvatarGroup>
 */
import React from 'react';
import {
  Avatar as MuiAvatar,
  AvatarGroup as MuiAvatarGroup,
  Badge as MuiBadge,
} from '@mui/material';
import { cn } from '@/lib/utils';

// ── Helpers ─────────────────────────────────────────────────────────────
const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 55%, 50%)`;
};

const sizeMap = {
  xs: { width: 24, height: 24, fontSize: '0.6rem' },
  sm: { width: 32, height: 32, fontSize: '0.75rem' },
  md: { width: 40, height: 40, fontSize: '0.875rem' },
  lg: { width: 52, height: 52, fontSize: '1.1rem' },
  xl: { width: 72, height: 72, fontSize: '1.5rem' },
};

const statusColors: Record<string, string> = {
  online: '#22c55e',
  offline: '#94a3b8',
  busy: '#ef4444',
  away: '#f59e0b',
};

// ── Props ───────────────────────────────────────────────────────────────
export interface AvatarProps {
  /** User name — used for initials fallback and background color */
  name?: string;
  /** Image src URL */
  src?: string;
  /** Alt text */
  alt?: string;
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Status indicator dot */
  status?: 'online' | 'offline' | 'busy' | 'away';
  /** Variant shape */
  variant?: 'circular' | 'rounded' | 'square';
  /** Click handler */
  onClick?: () => void;
  /** Custom background color override */
  bgColor?: string;
  /** Custom icon instead of initials */
  icon?: React.ReactNode;
  className?: string;
}

export interface AvatarGroupProps {
  children: React.ReactNode;
  /** Max visible avatars */
  max?: number;
  /** Size applied to all children */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Spacing — negative value for overlap */
  spacing?: 'small' | 'medium' | number;
  className?: string;
}

// ── Avatar ──────────────────────────────────────────────────────────────
export const Avatar: React.FC<AvatarProps> = ({
  name = '',
  src,
  alt,
  size = 'md',
  status,
  variant = 'circular',
  onClick,
  bgColor,
  icon,
  className = '',
}) => {
  const dims = sizeMap[size];
  const bg = bgColor || (name ? stringToColor(name) : '#94a3b8');

  const avatar = (
    <MuiAvatar
      src={src}
      alt={alt || name}
      variant={variant}
      onClick={onClick}
      sx={{
        ...dims,
        bgcolor: src ? undefined : bg,
        color: '#fff',
        fontWeight: 600,
        cursor: onClick ? 'pointer' : undefined,
        border: '2px solid',
        borderColor: 'background.paper',
      }}
      className={className}
    >
      {icon || (name ? getInitials(name) : null)}
    </MuiAvatar>
  );

  if (!status) return avatar;

  const dotSize = size === 'xs' || size === 'sm' ? 8 : 12;
  return (
    <MuiBadge
      overlap="circular"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      badgeContent={
        <span
          style={{
            width: dotSize,
            height: dotSize,
            borderRadius: '50%',
            backgroundColor: statusColors[status],
            border: '2px solid var(--bg-paper, #fff)',
            display: 'block',
          }}
        />
      }
    >
      {avatar}
    </MuiBadge>
  );
};

// ── AvatarGroup ─────────────────────────────────────────────────────────
export const AvatarGroup: React.FC<AvatarGroupProps> = ({
  children,
  max = 4,
  size,
  spacing = 'medium',
  className = '',
}) => (
  <MuiAvatarGroup
    max={max}
    spacing={spacing}
    className={className}
    sx={{
      '& .MuiAvatar-root': size ? sizeMap[size] : undefined,
    }}
  >
    {children}
  </MuiAvatarGroup>
);

export default Avatar;
